import { ChevronRightIcon } from "@heroicons/react/outline";
import { format } from "date-fns";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  PurchasesOrderItem,
  SuccessfulPurchase,
} from "../../../domain/usecases/get-account-balances";
import { db } from "../../../main/firebase";
import Loading from "../../components/loading/loading";
import { currentAccountState } from "../../components";
import { currencyFormatter } from "../../utils/helpers";
import { createReportExcelFile } from "./excel/excel";
import ExportToExcel from "./exportExcel";
import { reportsState } from "./atoms/atoms";
import ElectricityRow from "./electricityRow";

const getReceipt = (transaction: PurchasesOrderItem) => {
  const response = transaction.response as SuccessfulPurchase;
  return response?.creditVendReceipt ?? null;
};

export default function Electricity() {
  const { getCurrentAccount } = useRecoilValue(currentAccountState);
  const [state, setState] = useRecoilState(reportsState);
  const [receiptNumberFilter, setReceiptNumberFilter] = useState("");
  const [meterNumberFilter, setMeterNumberFilter] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [expandedTransactionId, setExpandedTransactionId] = useState("");

  const account = getCurrentAccount().account;

  useEffect(() => {
    setState((old) => ({
      ...old,
      isLoading: true,
    }));

    const q = query(
      collection(db, "purchaseOrderLineItem"),
      where("account", "==", account),
      where("itemType", "==", "ELECTRICITY"),
      where("dateCreated", ">=", state.startDate.startOf("day").toDate()),
      where("dateCreated", "<=", state.endDate.endOf("day").toDate())
    );

    return onSnapshot(q, (querySnapshot) => {
      const electricityPurchases = querySnapshot.docs
        .sort(
          (a, b) => a.data().dateUpdated.toDate() - b.data().dateUpdated.toDate()
        )
        .map((doc) => {
          return {
            account: doc.data().account,
            accountName: doc.data().accountName,
            amount: doc.data().amount,
            dateCreated: doc.data().dateCreated.toDate(),
            dateUpdated: doc.data().dateUpdated.toDate(),
            id: doc.data().id,
            number: doc.data().itemNumber,
            createdBy: doc.data().createdBy,
            approvedBy: doc.data().approvedBy,
            location: doc.data().location ? doc.data().location : null,
            name: doc.data().name ? doc.data().name : null,
            owner: doc.data().registeredOwner
              ? doc.data().registeredOwner
              : doc.data().owner,
            merchant: doc.data().merchant,
            itemType: doc.data().itemType,
            purchaseAmount: doc.data().purchaseAmount,
            lastPurchaseDate: doc.data().lastPurchaseDate,
            lastPurchaseAmount: doc.data().lastPurchaseAmount,
            jobTitle: doc.data().jobTitle ? doc.data().jobTitle : null,
            network: doc.data().network ? doc.data().network : null,
            company: {
              name: doc.data().company.name,
              companyNumber: doc.data().company.companyNumber,
              phoneNumber: doc.data().company.phoneNumber,
              postalAddress: doc.data().company.postalAddress,
              city: doc.data().company.city,
            },
            purchaseOrderNo: doc.data().purchaseOrderNo,
            purchaseOrderReference: doc.data().purchaseOrderReference,
            response: doc.data().response,
            status: doc.data().status,
          } as PurchasesOrderItem;
        })
        .filter((transaction) => transaction.status === "SUCCESSFUL")
        .filter((transaction) => !!getReceipt(transaction));

      setState((old) => ({
        ...old,
        isLoading: false,
        electricityPurchases,
      }));
    });
  }, [account, setState, state.startDate, state.endDate]);

  useEffect(() => {
    const receiptKeyword = receiptNumberFilter.trim().toLowerCase();
    const meterKeyword = meterNumberFilter.trim().toLowerCase();
    const customerKeyword = customerNameFilter.trim().toLowerCase();

    if (!receiptKeyword && !meterKeyword && !customerKeyword) {
      setState((old) => ({
        ...old,
        filteredElectricityPurchases: [],
      }));
      return;
    }

    const filteredElectricityPurchases = state.electricityPurchases.filter(
      (transaction) => {
        const receipt = getReceipt(transaction);
        const isReceiptMatch = receiptKeyword
          ? receipt?.receiptNo?.toLowerCase().includes(receiptKeyword)
          : true;
        const isMeterMatch = meterKeyword
          ? receipt?.meterNumber?.toLowerCase().includes(meterKeyword)
          : true;
        const isCustomerMatch = customerKeyword
          ? receipt?.name?.toLowerCase().includes(customerKeyword)
          : true;

        return !!(isReceiptMatch && isMeterMatch && isCustomerMatch);
      }
    );

    setState((old) => ({
      ...old,
      filteredElectricityPurchases,
    }));
  }, [
    customerNameFilter,
    meterNumberFilter,
    receiptNumberFilter,
    setState,
    state.electricityPurchases,
  ]);

  const isFiltered =
    receiptNumberFilter.trim().length > 0 ||
    meterNumberFilter.trim().length > 0 ||
    customerNameFilter.trim().length > 0;

  const electricityPurchasesList = isFiltered
    ? state.filteredElectricityPurchases
    : state.electricityPurchases;

  const transactionsSheet = async (
    transactions,
    workbook,
    logo,
    reportStartDate,
    reportEndDate,
    merchantName
  ) => {
    const startDate = format(reportStartDate.toDate(), "yyyy-MM-dd");
    const endDate = format(reportEndDate.toDate(), "yyyy-MM-dd");
    const worksheet = workbook.addWorksheet("Electricity", {
      properties: {
        showGridLines: false,
      },
      pageSetup: {
        paperSize: 9,
        fitToPage: true,
        orientation: "landscape",
      },
    });

    worksheet.mergeCells("A1", "D2");
    worksheet.addImage(logo, {
      tl: {
        col: 0.25,
        row: 0.25,
      },
      ext: {
        width: 150,
        height: 32,
      },
    });

    worksheet.getCell("A4").value = `For ${merchantName}`;
    worksheet.getCell("A6").value = "Electricity Transactions";
    worksheet.getCell("A8").value = `Date From ${startDate} to ${endDate}`;
    worksheet.getRow(6).font = { name: "Calibri", size: 16, bold: true };

    const headers = worksheet.getRow(10);
    headers.values = [
      "Date Created",
      "Receipt No",
      "Meter No",
      "Customer Name",
      "STS Token Data",
      "Units (KWh)",
      "Tarriff Index",
      "Cost of Units",
      "Government Levy",
      "Fixed Charge",
      "VAT",
      "Transaction Amount",
    ];

    headers.eachCell((cell) => {
      cell.font = {
        name: "Calibri",
        size: 10,
        bold: true,
        color: { argb: "FFFFFFFF" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF002060" },
      };
    });

    worksheet.columns = [
      { key: "dateCreated", width: 22 },
      { key: "receiptNo", width: 16 },
      { key: "meterNumber", width: 18 },
      { key: "customerName", width: 24 },
      { key: "stsCipher", width: 32 },
      { key: "tokenUnits", width: 14 },
      { key: "tariffIndex", width: 14 },
      { key: "costUnits", width: 16 },
      { key: "governmentLevy", width: 18 },
      { key: "standardCharge", width: 16 },
      { key: "vat", width: 12 },
      { key: "amount", width: 20 },
    ];

    transactions.forEach((transaction) => {
      const receipt = getReceipt(transaction);

      worksheet.addRow({
        dateCreated: format(new Date(transaction.dateUpdated), "yyyy-MM-dd HH:mm:ss"),
        receiptNo: receipt?.receiptNo ?? "-",
        meterNumber: receipt?.meterNumber ?? "-",
        customerName: receipt?.name ?? "-",
        stsCipher: receipt?.stsCipher ?? "-",
        tokenUnits: receipt?.tokenUnits ?? "-",
        tariffIndex: receipt?.ti ?? receipt?.tariff ?? "-",
        costUnits: Number(receipt?.costUnits ?? 0),
        governmentLevy: Number(receipt?.governmentLevy ?? 0),
        standardCharge: Number(receipt?.standardCharge ?? 0),
        vat: Number(receipt?.vat ?? 0),
        amount: Number(receipt?.amtTendered ?? 0),
      });
    });

    [8, 9, 10, 11, 12].forEach((columnIndex) => {
      worksheet.getColumn(columnIndex).numFmt = "0.00";
    });

    const firstDataRow = 11;
    const lastDataRow = firstDataRow + transactions.length - 1;
    const totalCell =
      transactions.length > 0
        ? { formula: `SUM(L${firstDataRow}:L${lastDataRow})` }
        : 0;

    const totalsRow = worksheet.addRow([
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "Total",
      totalCell,
    ]);

    totalsRow.eachCell((cell) => {
      cell.font = {
        name: "Calibri",
        size: 10,
        bold: true,
        color: { argb: "FFFFFFFF" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF002060" },
      };
    });
  };

  const exportToExcel = async () => {
    await createReportExcelFile(
      "Electricity Report",
      electricityPurchasesList,
      transactionsSheet,
      getCurrentAccount().merchantName ? getCurrentAccount().merchantName : "",
      state.startDate,
      state.endDate
    );
  };

  return (
    <>
      <div className="w-full flex flex-col lg:max-w-7xl bg-white overflow-hidden shadow rounded-sm">
        <div className="px-6 py-4">
          <div className="mb-0">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
              <div className="sm:col-span-1 my-auto align-middle">
                <h2 className="mx-auto text-lg leading-6 font-medium text-gray-900">
                  Filter:
                </h2>
              </div>

              <div className="sm:col-span-2">
                <div className="border-b border-gray-300 focus-within:border-blue-600">
                  <input
                    onChange={(e) => setReceiptNumberFilter(e.target.value)}
                    type="text"
                    name="receiptNumber"
                    id="receiptNumber"
                    className="block w-full border-0 py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                    placeholder="Receipt No"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <div className="border-b border-gray-300 focus-within:border-blue-600">
                  <input
                    onChange={(e) => setMeterNumberFilter(e.target.value)}
                    type="text"
                    name="meterNumber"
                    id="meterNumber"
                    className="block w-full border-0 py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                    placeholder="Meter No"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <div className="border-b border-gray-300 focus-within:border-blue-600">
                  <input
                    onChange={(e) => setCustomerNameFilter(e.target.value)}
                    type="text"
                    name="customerName"
                    id="customerName"
                    className="block w-full border-0 py-2 border-b border-transparent bg-gray-50 focus:border-blue-600 focus:ring-0 sm:text-sm"
                    placeholder="Customer Name"
                  />
                </div>
              </div>

              <div className="sm:col-span-5 flex justify-end">
                {electricityPurchasesList.length > 0 && (
                  <button onClick={() => exportToExcel()} type="button" className="pr-3 bg-white">
                    <ExportToExcel />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-grow">
          <div className="shadow sm:hidden w-full">
            {electricityPurchasesList.length > 0 ? (
              <ul className="mt-2 divide-y divide-gray-200 overflow-hidden shadow sm:hidden">
                {electricityPurchasesList.map((transaction) => {
                  const receipt = getReceipt(transaction);
                  const isExpanded = expandedTransactionId === transaction.id;

                  return (
                    <li key={transaction.id}>
                      <button
                        type="button"
                        className="w-full px-4 py-4 bg-white hover:bg-gray-50 text-left"
                        onClick={() =>
                          setExpandedTransactionId(isExpanded ? "" : transaction.id)
                        }
                      >
                        <span className="flex items-start justify-between space-x-4">
                          <span className="flex-1 flex flex-col text-sm text-gray-500 truncate">
                            <span className="text-gray-900 font-medium">
                              {receipt?.meterNumber ?? "-"}
                            </span>
                            <span>{format(new Date(transaction.dateUpdated), "yyyy-MM-dd HH:mm:ss")}</span>
                            <span className="text-gray-900 font-medium">
                              {currencyFormatter(Number(receipt?.amtTendered ?? 0))}
                            </span>

                            {isExpanded && (
                              <span className="mt-2 space-y-1 text-gray-500">
                                <span className="block">Receipt: {receipt?.receiptNo ?? "-"}</span>
                                <span className="block">Name: {receipt?.name ?? "-"}</span>
                                <span className="block">Units: {receipt?.tokenUnits ?? "-"}</span>
                                <span className="block">Token: {receipt?.stsCipher ?? "-"}</span>
                              </span>
                            )}
                          </span>
                          <ChevronRightIcon
                            className={`flex-shrink-0 h-5 w-5 text-gray-400 ${
                              isExpanded ? "transform rotate-90" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <h2 className="text-md leading-6 font-medium text-gray-900">
                No results found for the selected dates/filters.
              </h2>
            )}
          </div>

          <div className="flex-grow hidden sm:flex">
            <div className="flex-grow max-w-7xl mx-auto">
              <div className="flex flex-col mt-2">
                <div className="align-middle min-w-full flex-grow overflow-x-auto overflow-hidden">
                  {electricityPurchasesList.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="pr-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Receipt No
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Meter No
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Customer Name
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            STS Token Data
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Units (KWh)
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Tarriff Index
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Cost of Units
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Government Levy
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            Fixed Charge
                          </th>
                          <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">
                            VAT
                          </th>
                          <th className="px-6 py-3 text-right text-sm font-medium text-gray-500 tracking-wider">
                            Transaction Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {electricityPurchasesList.map((transaction) => (
                          <ElectricityRow key={transaction.id} transaction={transaction} />
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <h2 className="text-md leading-6 font-medium text-gray-900">
                      No results found for the selected dates/filters.
                    </h2>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {state.isLoading && <Loading />}
    </>
  );
}
